// Campaigns › Campaigns, the list (step 2.30; docs/specs/CAMPAIGN_PAGES_2026_10.md section 1): a card per campaign
// with its status, when it launched, its counts and its share of sends, editable on the card (the others rebalance in
// proportion and the total stays 100; setCampaignShares). A ⋮ menu edits, pauses or starts, copies or archives it
// (archiving pauses a running campaign first; decideCampaign). Archived campaigns sit at the bottom, greyed out, with
// Copy and Restore (reactivateCampaign: it goes back through approval). With no campaigns yet, the business's work
// shows as one "Main campaign". #360: each running campaign shows the next overnight drafting run.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import { copyCampaign, decideCampaign, reactivateCampaign, setCampaignShares, type Campaign } from "../../lib/campaigns";
import { reachOf } from "../../lib/campaignReach";
import { rebalance, sharesOf, sharing, STATUS_LABEL } from "../../lib/campaignEditor";
import type { EnginePart } from "../../lib/engineSchedule";
import { fmtDate, fmtDateTime, fmtNumber } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { Menu, type MenuItem } from "../kit";
import { Notice } from "../ui";
import "./campaigns.css";

export const campaignLink = (id: string) => `/campaigns/list?campaign=${encodeURIComponent(id || "main")}`;

/** The engine's schedule, or null when it can't be read (the campaign pages still work without it). */
export function useEngineSchedule(crm: Crm) {
  return useAsync(async () => {
    try {
      return await crm.engineSchedule();
    } catch {
      return null;
    }
  }, [crm]);
}

/** 4.11 The outreach agent's part that drafts every campaign's steps. */
export const draftPart = (parts: EnginePart[] | undefined) => parts?.find((p) => p.agent === "outreach-agent" && p.job === "draft") ?? null;

/** #360 When a running campaign is next drafted; null when it is not running or the schedule is not known. */
export function nextRunLine(c: Pick<Campaign, "status" | "share">, draft: EnginePart | null): string | null {
  if (c.status !== "approved" || c.share <= 0 || !draft) return null;
  return draft.enabled && draft.next_run_at ? `Next run ${fmtDateTime(draft.next_run_at)}` : "Drafting is switched off";
}

type Changed = (msg: string, tone?: "ok" | "bad") => void;

/** Newest or oldest first, by when it launched (else when it was made); the main campaign keeps its place. */
export type CampaignSort = "newest" | "oldest";
const ageOf = (c: Campaign) => Date.parse(c.launched_at ?? c.created_at ?? "") || 0;
export function sortCampaigns(list: Campaign[], sort: CampaignSort): Campaign[] {
  const dir = sort === "newest" ? -1 : 1;
  return [...list].sort((a, b) => dir * (ageOf(a) - ageOf(b)));
}

/** Active: running or waiting for your approval. */
export const isActive = (c: Campaign) => c.status === "approved" || c.status === "proposed";

/** Live campaigns (everything not retired) and archived ones (retired). */
export function splitCampaigns(list: Campaign[]): { live: Campaign[]; archived: Campaign[] } {
  return { live: list.filter((c) => c.status !== "retired"), archived: list.filter((c) => c.status === "retired") };
}

const STATUS_CLASS: Record<Campaign["status"], string> = {
  approved: "is-running", proposed: "is-waiting", paused: "is-paused", draft: "is-draft", retired: "is-archived",
};

function counts(c: Campaign): { label: string; n: number }[] {
  const n = c.counts;
  if (reachOf(c) === "known") return [{ label: "Due", n: n.due ?? 0 }, { label: "Followed up", n: n.following_up ?? 0 }, { label: "Replied", n: n.engaged }];
  return [{ label: "Lined up", n: n.planned }, { label: "Contacted", n: n.in_sequence }, { label: "Replied", n: n.engaged }];
}

/** The share box: editable for a running campaign that shares the pace with others; read only otherwise. */
function ShareBox({ c, share, editable, onSet }: { c: Campaign; share: number; editable: boolean; onSet: (v: number) => void }) {
  const [text, setText] = useState(String(share));
  useEffect(() => setText(String(share)), [share]);
  if (reachOf(c) === "known") return <span className="chip">Always on</span>;
  const commit = () => {
    const v = Math.round(Number(text));
    if (!Number.isFinite(v) || v === share) return setText(String(share));
    onSet(v);
  };
  return (
    <label className={`camp-share${editable ? "" : " is-fixed"}`} title={editable ? "Share of sends" : c.status === "approved" ? "The only running campaign takes all of it" : "Takes a share once it runs"}>
      <input
        type="text"
        inputMode="numeric"
        aria-label={`Share of sends for ${c.name}`}
        value={text}
        disabled={!editable}
        onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setText(String(share));
        }}
      />
      <span aria-hidden="true">%</span>
    </label>
  );
}

function CampaignCard({ c, crm, share, shareEditable, next, onShare, onChanged }: {
  c: Campaign; crm: Crm; share: number; shareEditable: boolean; next: string | null; onShare: (v: number) => void; onChanged: Changed;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const known = reachOf(c) === "known";
  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onChanged(what);
    } catch (e) {
      onChanged((e as Error).message, "bad");
    } finally {
      setBusy(false);
    }
  };
  const copy = () =>
    run(`Copied ${c.name}.`, async () => {
      const r = await copyCampaign(crm, c.id, `${c.name} (copy)`);
      navigate(campaignLink(r.id));
    });
  // Archiving a running campaign pauses it first, so nothing more goes out before it is put away.
  const archive = () =>
    run(`${c.name} is archived.`, async () => {
      if (c.status === "approved") await decideCampaign(crm, c.id, "pause");
      await decideCampaign(crm, c.id, "retire");
    });
  // An archived campaign goes back through approval like a new one; one with an unfinished offer opens as a draft.
  const restore = async () => {
    setBusy(true);
    try {
      const r = await reactivateCampaign(crm, c.id);
      onChanged(r?.proposed ? `${c.name} is back and waiting for your approval.` : `${c.name} is back as a draft. Finish its offer, then approve it.`);
      if (!r?.proposed) navigate(campaignLink(c.id));
    } catch (e) {
      onChanged((e as Error).message, "bad");
    } finally {
      setBusy(false);
    }
  };
  const items: MenuItem[] = c.status === "retired"
    ? [...(known ? [] : [{ label: "Copy", onSelect: copy, disabled: busy }]), { label: "Restore", onSelect: restore, disabled: busy }]
    : [
        { label: "Edit campaign", onSelect: () => navigate(campaignLink(c.id)) },
        ...(c.status === "approved" ? [{ label: "Pause", onSelect: () => run(`${c.name} is paused.`, () => decideCampaign(crm, c.id, "pause")), disabled: busy }] : []),
        ...(c.status === "paused" ? [{ label: "Start", onSelect: () => run(`${c.name} is running again.`, () => decideCampaign(crm, c.id, "resume")), disabled: busy }] : []),
        ...(known ? [] : [{ label: "Copy", onSelect: copy, disabled: busy }]),
        { label: "Archive", onSelect: archive, danger: true, disabled: busy, note: c.status === "approved" ? "Pauses it first" : undefined },
      ];
  return (
    <article className={`camp-card ${STATUS_CLASS[c.status]}`} aria-label={c.name}>
      <div className="camp-top">
        <h3 className="camp-title"><Link to={campaignLink(c.id)}>{c.name}</Link></h3>
        {c.status !== "retired" && <ShareBox c={c} share={share} editable={shareEditable && !busy} onSet={onShare} />}
        {c.id && <Menu label={`Actions for ${c.name}`} items={items} />}
      </div>
      <p className="camp-meta">
        <span className={`camp-status ${STATUS_CLASS[c.status]}`}>{c.status === "retired" ? "Archived" : STATUS_LABEL[c.status]}</span>
        <span className="muted">{c.launched_at ? `Launched ${fmtDate(c.launched_at)}` : c.id ? "Not launched yet" : "All your work outside a campaign"}</span>
      </p>
      {next && <p className="camp-next muted small">{next}</p>}
      <dl className="camp-counts">
        {counts(c).map((x) => (
          <div key={x.label}><dt>{x.label}</dt><dd>{fmtNumber(x.n)}</dd></div>
        ))}
      </dl>
    </article>
  );
}

/** Every campaign as a card, archived ones last; `activeOnly` hides the rest. */
export function CampaignCards({ list, crm, sort, activeOnly, onChanged }: {
  list: Campaign[]; crm: Crm; sort: CampaignSort; activeOnly: boolean; onChanged: Changed;
}) {
  const draft = draftPart(useEngineSchedule(crm).data?.parts);
  const { live, archived } = splitCampaigns(sortCampaigns(list, sort));
  const shown = activeOnly ? live.filter(isActive) : live;
  const shared = sharing(list).filter((c) => c.id);
  const saved = sharesOf(shared);
  const [shares, setShares] = useState<Record<string, number>>(saved);
  const key = shared.map((c) => `${c.id}:${c.share}`).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- key stands for the campaigns' shares
  useEffect(() => setShares(saved), [key]);
  const setShare = async (id: string, v: number) => {
    const next = rebalance(shares, id, v);
    setShares(next);
    try {
      await setCampaignShares(crm, next);
      onChanged(`Shares saved: ${shared.map((c) => `${c.name} ${next[c.id] ?? 0}%`).join(", ")}.`);
    } catch (e) {
      setShares(saved);
      onChanged((e as Error).message, "bad");
    }
  };
  const card = (c: Campaign) => (
    <CampaignCard
      key={c.id || "main"}
      c={c}
      crm={crm}
      share={c.id in shares ? shares[c.id]! : c.status === "approved" ? Math.round(c.share) : 0}
      shareEditable={c.id in shares && shared.length > 1}
      next={nextRunLine(c, draft)}
      onShare={(v) => setShare(c.id, v)}
      onChanged={onChanged}
    />
  );
  return (
    <>
      {shared.length > 1 && <p className="muted small">Share of sends across running campaigns adds up to 100%. Change one and the others rebalance.</p>}
      {shown.length ? (
        <div className="camp-grid">{shown.map(card)}</div>
      ) : (
        <p className="muted small">{activeOnly ? "No campaign is running or waiting for you." : "No campaign is live. Restore an archived one below, or add a new campaign."}</p>
      )}
      {!activeOnly && archived.length > 0 && (
        <section aria-labelledby="archived-campaigns" className="camp-archived">
          <h2 id="archived-campaigns" className="camp-group">Archived ({archived.length})</h2>
          <div className="camp-grid">{archived.map(card)}</div>
        </section>
      )}
    </>
  );
}

export function ListNotice({ msg }: { msg: { text: string; tone: "ok" | "bad" } | null }) {
  return <Notice text={msg?.text ?? null} tone={msg?.tone} />;
}
